<template>
  <div
    class="AuthRegister"
    data-cy-id="register-stage-personal-info"
    data-rmpdev-selector="update-user-form"
  >
    <h1 class="u-noMargin">
      Your personal information
    </h1>
    <p>Take 60 seconds to tell us about yourself. And we’ll only send you jobs you want to apply for.</p>

    <transition>
      <div
        v-if="serverValidationErrors.fail"
        class="Notice Notice--danger"
      >
        <div class="Notice-copy">
          {{ serverValidationErrors.fail }}
        </div>
      </div>
    </transition>

    <form
      ref="registration"
      class="Form"
    >
      <form-autocomplete
        v-model="fields.gender"
        :value="user.gender"
        :items="genders"
        :manual-validate="validateAll"
        :error="validateStatus({ field: v$.fields.gender, serverKey: 'gender', label: 'Gender' })"
        label="Gender"
        required
        data-rmpdev-field-selector="gender"
        placeholder="Select which gender you identify with"
        data-cy-id="register-gender"
        @rmp:change="validateBlur({ field: v$.fields.gender, serverKey: 'gender' })"
      />

      <form-autocomplete
        v-model="fields.institution_start_year"
        :error="validateStatus({ field: v$.fields.institution_start_year, serverKey: 'institution_start_year', label: 'Secondary school start year' })"
        :manual-validate="validateAll"
        :items="institutionStartYears"
        label="Secondary school start year"
        required
        data-rmpdev-field-selector="institution_start_year"
        placeholder="Select your school year"
        data-cy-id="register-start-year"
        @rmp:change="validateBlur({ field: v$.fields.institution_start_year, serverKey: 'institution_start_year' })"
      />
      <div data-rmpdev-field-selector="userLocation">
        <div
          class="Form-field"
          data-cy-id="register-uk-based"
        >
          <label>Are you based in the UK? *</label>
          <form-radio
            v-model="fields.uk_based"
            :value="1"
            label="Yes"
          />
          <form-radio
            v-model="fields.uk_based"
            :value="0"
            label="No"
          />
          <error-messages
            :manual-validate="validateAll"
            :errors="validateStatus({ field: v$.fields.uk_based, serverKey: 'uk_based', label: 'Location' }).errors"
          />
        </div>

        <form-input
          v-if="fields.uk_based === 1"
          v-model="fields.postcode"
          data-cy-id="register-postcode"
          :error="validateStatus({ field: v$.fields.postcode, serverKey: 'postcode', label: 'Postcode' })"
          :manual-validate="validateAll"
          required
          label="Postcode"
          input-type="text"
          @change="validateBlur({ field: v$.fields.postcode, serverKey: 'postcode' })"
        />
      </div>

      <button
        :class="['Button Button--primary Button--long', {'is-loading' : ui.loading}]"
        data-cy-id="register-next"
        @click.prevent="updateUser"
      >
        Next
      </button>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import FormAutocomplete from '@/components/form/autocomplete-select';
import FormSelect from '@/components/form/select';
import FormRadio from '@/components/form/radio-button';
import FormInput from '@/components/form/input';
import ErrorMessages from '@/components/form/partials/error-message';
import RegistrationStagesMixin from './registration-stages-mixin';

export default {
  components: {
    FormAutocomplete,
    FormSelect,
    FormRadio,
    FormInput,
    ErrorMessages,
  },

  mixins: [RegistrationStagesMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      fields: {
        institution_start_year: '',
        gender: null,
        uk_based: null,
        postcode: null,
      },
    };
  },
  validations() {
    const rules = {
      fields: {
        institution_start_year: {
          required,
        },
        gender: {
          required,
        },
        uk_based: {
          required,
        },
      },
    };

    if (this.fields.uk_based === 1) {
      rules.fields.postcode = { required };
    }

    return rules;
  },
  computed: {
    ...mapState('entities', {
      institutionStartYears: (state) => state.institutionStartYears,
      genders: (state) => state.genders,
    }),
    updatePayload() {
      return {
        institution_start_year: this.fields.institution_start_year.value,
        gender: this.fields.gender.value,
        uk_based: this.fields.uk_based,
        postcode: this.fields.postcode,
      };
    },
  },
  mounted() {
    Promise.all([
      this.getInstitutionStartYears(),
      this.getGenders(),
    ]).then(() => {
      this.fields.institution_start_year = this.user.institution_start_year === 0 ? null : this.user.institution_start_year;
      this.fields.gender = this.genders.find((obj) => obj.value === this.user.gender);
      this.fields.uk_based = this.user.uk_based;
      this.fields.postcode = this.user.postcode;
    });
  },

  methods: {
    ...mapActions('entities', {
      getInstitutionStartYears: 'getInstitutionStartYears',
      getGenders: 'getGenders',
    }),
  },
};
</script>
